import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import {
  Alert, Button, Card, EmptyState, Field, Modal, Spinner, StarRating, StatusPill, formatDate, formatTime,
} from '../../components/ui';
import { useToast } from '../../components/Toast';

const COMPLETABLE = ['assigned', 'confirmed'];
const today = () => new Date().toISOString().slice(0, 10);

function BookingCard({ booking, t, onComplete, onReview, busy }) {
  const canComplete = COMPLETABLE.includes(booking.status) && booking.bookingDate <= today();
  const canReview = booking.status === 'completed' && !booking.myReviewSubmitted;
  return (
    <article className="booking-card">
      <header>
        <div>
          <strong>{formatDate(booking.bookingDate, { long: true })}</strong>
          <span className="muted"> · {formatTime(booking.startTime)} – {formatTime(booking.endTime)}</span>
        </div>
        <StatusPill status={booking.status} label={booking.statusLabel} />
      </header>
      <dl className="detail-grid">
        <div><dt>{t('nannyBookings.family')}</dt><dd>{booking.family.familyName || booking.family.name}</dd></div>
        <div><dt>{t('nannyBookings.contact')}</dt><dd>{booking.family.name}{booking.family.phone ? ` · ${booking.family.phone}` : ''}</dd></div>
        <div><dt>{t('nannyBookings.location')}</dt><dd>{[booking.family.addressLine1, booking.family.suburb, booking.family.city].filter(Boolean).join(', ') || '—'}</dd></div>
        <div><dt>{t('nannyBookings.children')}</dt><dd>{booking.childrenCount}</dd></div>
        <div><dt>{t('nannyBookings.requirement')}</dt><dd>{booking.requirement || '—'}</dd></div>
        <div><dt>{t('nannyBookings.reference')}</dt><dd>{booking.reference}</dd></div>
      </dl>
      {booking.notes && (
        <p className="note-block"><strong>{t('nannyBookings.familyNotes')}</strong> {booking.notes}</p>
      )}
      {booking.status === 'cancelled' && booking.cancelReason && (
        <p className="note-block"><strong>{t('nannyBookings.cancellationReason')}</strong> {booking.cancelReason}</p>
      )}
      {canComplete && onComplete && (
        <label className="check">
          <input type="checkbox" disabled={busy} onChange={() => onComplete(booking)} />
          {t('nannyBookings.markComplete')}
        </label>
      )}
      {booking.status === 'completed' && (
        booking.myReviewSubmitted ? (
          <p className="muted small">{t('nannyBookings.reviewSubmitted')}</p>
        ) : (
          canReview && onReview && (
            <Button type="button" variant="ghost" onClick={() => onReview(booking)}>
              {t('nannyBookings.leaveReview')}
            </Button>
          )
        )
      )}
    </article>
  );
}

function ReviewModal({ booking, onClose, onSubmit, busy, t }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  function submit() {
    if (!rating) return;
    onSubmit({ rating, comment });
  }

  return (
    <Modal title={t('nannyBookings.reviewModalTitle')} onClose={onClose}>
      <p className="muted">{t('nannyBookings.reviewModalSubtitle')}</p>
      <StarRating value={rating} onChange={setRating} />
      <Field label={t('nannyBookings.reviewCommentLabel')}>
        <textarea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('nannyBookings.reviewCommentPlaceholder')}
        />
      </Field>
      <div className="form-footer">
        <Button type="button" onClick={submit} loading={busy} disabled={!rating}>
          {t('nannyBookings.submitReview')}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('nannyBookings.cancel')}
        </Button>
      </div>
    </Modal>
  );
}

export default function NannyBookings() {
  const { t } = useTranslation();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  function load() {
    api.get('/api/nanny/bookings').then(setData).catch((err) => setError(err.message));
  }

  useEffect(load, []);

  async function complete(booking) {
    if (!window.confirm(t('nannyBookings.confirmComplete'))) return;
    setBusy(true);
    try {
      await api.post(`/api/nanny/bookings/${booking.id}/complete`);
      toast.success(t('nannyBookings.shiftMarkedComplete'));
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitReview({ rating, comment }) {
    setReviewBusy(true);
    try {
      await api.post(`/api/nanny/bookings/${reviewBooking.id}/review`, { rating, comment });
      toast.success(t('nannyBookings.reviewThanks'));
      setReviewBooking(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReviewBusy(false);
    }
  }

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!data) return <Spinner />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('nannyBookings.pageTitle')}</h1>
          <p className="muted">{t('nannyBookings.subtitle')}</p>
        </div>
      </div>

      <Card title={t('nannyBookings.upcoming', { count: data.upcoming.length })}>
        {data.upcoming.length ? (
          data.upcoming.map((b) => (
            <BookingCard key={b.id} booking={b} t={t} onComplete={complete} onReview={setReviewBooking} busy={busy} />
          ))
        ) : (
          <EmptyState title={t('nannyBookings.nothingScheduled')}>
            {t('nannyBookings.nothingScheduledBody')}
          </EmptyState>
        )}
      </Card>

      <Card title={t('nannyBookings.previousBookings', { count: data.past.length })}>
        {data.past.length ? (
          data.past.map((b) => (
            <BookingCard key={b.id} booking={b} t={t} onComplete={complete} onReview={setReviewBooking} busy={busy} />
          ))
        ) : (
          <EmptyState title={t('nannyBookings.noPastBookings')} />
        )}
      </Card>

      {reviewBooking && (
        <ReviewModal
          booking={reviewBooking}
          onClose={() => setReviewBooking(null)}
          onSubmit={submitReview}
          busy={reviewBusy}
          t={t}
        />
      )}
    </>
  );
}
